import React, { useEffect, type ReactNode } from 'react';
import { observer } from 'mobx-react';
import { ThemeProvider } from '@emotion/react';
import { useTheme } from 'hooks/use-theme';
import { THEME_STORAGE_KEY } from 'stores/mobx/user.store';
import lightTheme, { darkTheme, type Env0Theme } from 'constants/themes.constants';

/**
 * The dark design tokens only apply under [data-theme='dark'], and the APO-452 type ramp only under
 * [data-type-ramp='v2']. Storybook's decorator calls this too, so the two stay in step.
 */
export const syncDocumentTheme = (theme: Env0Theme) => {
  document.documentElement.dataset.theme = theme.mode;
  document.documentElement.dataset.typeRamp = theme.isNewDesign ? 'v2' : 'legacy';
};

/**
 * The pre-React entry point, called from index.tsx before the first paint — the mobx store does not
 * exist yet, so the mode comes straight from storage. Both attributes have to be set there: setting
 * only [data-theme] leaves the first paint on the legacy ramp until the effect below corrects it.
 */
export const syncDocumentThemeFromStorage = () =>
  syncDocumentTheme(localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? darkTheme : lightTheme);

const ThemeProviderWrapper: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const { appTheme } = useTheme();

  useEffect(() => {
    syncDocumentTheme(appTheme);
  }, [appTheme]);

  return <ThemeProvider theme={appTheme}>{children}</ThemeProvider>;
};

export default observer(ThemeProviderWrapper);
